import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { Fragment, useEffect, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { api, type HelpBotTranscript } from '../services/api'
import { periodToDays, useAnalyticsPeriod } from '../lib/analytics-period'

type UserType = '' | 'visitor' | 'nonmember' | 'member'
type OrderBy = 'recent' | 'tokens'

const PAGE_SIZE = 25

/** Visitor / non-member / member are the three tiers the Help Bot meters
 *  separately, so they are worth telling apart at a glance rather than reading. */
const USER_TYPE_BADGE: Record<string, string> = {
  visitor: 'badge-ghost',
  nonmember: 'badge-warning',
  member: 'badge-success',
}

function tokens(value: number | null): string {
  // Null means "not measured" — an uninstrumented path — which is a different
  // fact from "cost nothing", so it must not render as 0.
  if (value === null || value === undefined) return '—'
  return value.toLocaleString()
}

export default function HelpBotSessions() {
  const { period } = useAnalyticsPeriod()
  const [search, setSearch] = useState('')
  const [submittedSearch, setSubmittedSearch] = useState('')
  const [userType, setUserType] = useState<UserType>('')
  const [orderBy, setOrderBy] = useState<OrderBy>('recent')
  const [page, setPage] = useState(0)
  const [expanded, setExpanded] = useState<string | null>(null)

  useEffect(() => setPage(0), [period])

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['help-bot-transcripts', submittedSearch, userType, period, orderBy, page],
    queryFn: () =>
      api.getHelpBotTranscripts({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        search: submittedSearch || undefined,
        user_type: userType || undefined,
        since_days: periodToDays(period),
        order_by: orderBy,
      }),
    staleTime: 30000,
    // Keep the table on screen while a new page loads, so filtering does not
    // flash an empty state between results.
    placeholderData: keepPreviousData,
  })

  const applySearch = (event: React.FormEvent) => {
    event.preventDefault()
    setPage(0)
    setSubmittedSearch(search.trim())
  }

  const resetTo = <T,>(setter: (value: T) => void) => (value: T) => {
    setPage(0)
    setter(value)
  }

  const transcripts = data?.transcripts ?? []
  const total = data?.total ?? 0
  const spend = data?.token_spend
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1)

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Help Bot Sessions</h1>
          <p className="text-base-content/60 text-sm mt-1">
            Every Help Bot exchange, including anonymous visitors. Question and answer
            text lives here and is not sent to analytics.
          </p>
        </div>
        <button className="btn btn-sm" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {spend && (
        <div className="stats stats-horizontal shadow w-full overflow-x-auto">
          <div className="stat">
            <div className="stat-title">Exchanges</div>
            <div className="stat-value text-2xl tabular-nums">{total.toLocaleString()}</div>
            <div className="stat-desc">matching the current filter</div>
          </div>
          <div className="stat">
            <div className="stat-title">Avg tokens</div>
            <div className="stat-value text-2xl tabular-nums">{tokens(spend.avg_total_tokens)}</div>
            <div className="stat-desc">
              in {tokens(spend.avg_input_tokens)} · out {tokens(spend.avg_output_tokens)}
            </div>
          </div>
          <div className="stat">
            <div className="stat-title">Avg model calls</div>
            <div className="stat-value text-2xl tabular-nums">{tokens(spend.avg_model_calls)}</div>
            <div className="stat-desc">per answer</div>
          </div>
          <div className="stat">
            <div className="stat-title">Total tokens</div>
            <div className="stat-value text-2xl tabular-nums">{tokens(spend.sum_total_tokens)}</div>
            <div className="stat-desc">across all matches</div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <form onSubmit={applySearch} className="join">
          <input
            type="text"
            className="input input-bordered input-sm join-item w-64"
            placeholder="Search questions and answers"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit" className="btn btn-sm join-item">Search</button>
        </form>

        <select
          className="select select-bordered select-sm"
          value={userType}
          onChange={(e) => resetTo(setUserType)(e.target.value as UserType)}
        >
          <option value="">All users</option>
          <option value="visitor">Visitors</option>
          <option value="nonmember">Non-members</option>
          <option value="member">Members</option>
        </select>

        <select
          className="select select-bordered select-sm"
          value={orderBy}
          onChange={(e) => resetTo(setOrderBy)(e.target.value as OrderBy)}
        >
          <option value="recent">Most recent</option>
          <option value="tokens">Most expensive</option>
        </select>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{(error as Error).message}</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : transcripts.length === 0 ? (
        <div className="text-center py-16 text-base-content/60">
          No Help Bot exchanges match these filters.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Question</th>
                <th>Intent</th>
                <th className="text-right">Sources</th>
                <th className="text-right">Tokens</th>
              </tr>
            </thead>
            <tbody>
              {transcripts.map((t: HelpBotTranscript) => {
                const isOpen = expanded === t.id
                return (
                  <Fragment key={t.id}>
                    <tr
                      className="hover cursor-pointer"
                      onClick={() => setExpanded(isOpen ? null : t.id)}
                    >
                      <td className="whitespace-nowrap text-base-content/70">
                        {formatDistanceToNow(new Date(t.created_at), { addSuffix: true })}
                      </td>
                      <td className="whitespace-nowrap">
                        <span className={`badge badge-sm ${USER_TYPE_BADGE[t.user_type] ?? 'badge-ghost'}`}>
                          {t.user_type}
                        </span>
                        {/* A visitor has no identity beyond their token — say so
                            rather than rendering a blank cell.

                            The separator is a real character, not just the ml-2
                            margin. On screen the gap was enough, but a margin is
                            not part of the text: copying a row out of this table
                            produced "membernate.bulcroft@asminternational.org",
                            an address that does not exist, which then travelled
                            into a bug report as though it did. Anything pasted
                            out of an admin table gets read as data. */}
                        <span className="ml-2 text-xs text-base-content/60">
                          <span aria-hidden="true">· </span>
                          {t.email || t.name || 'anonymous'}
                        </span>
                      </td>
                      <td className="max-w-md truncate">{t.question}</td>
                      <td className="text-base-content/70">{t.intent || '—'}</td>
                      <td className="text-right tabular-nums">{t.source_count}</td>
                      <td className="text-right tabular-nums">{tokens(t.total_tokens)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-base-200">
                        <td colSpan={6} className="whitespace-pre-wrap align-top">
                          <div className="space-y-3 py-2">
                            <div>
                              <div className="text-xs uppercase tracking-wide text-base-content/60 mb-1">
                                Question
                              </div>
                              <div>{t.question}</div>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wide text-base-content/60 mb-1">
                                Answer
                              </div>
                              <div>{t.answer || <span className="text-base-content/50">No answer recorded</span>}</div>
                            </div>
                            <div className="text-xs text-base-content/60 flex flex-wrap gap-4">
                              <span>in {tokens(t.input_tokens)}</span>
                              <span>out {tokens(t.output_tokens)}</span>
                              <span>{tokens(t.model_calls)} model call(s)</span>
                              <span>id {t.id}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-base-content/60 tabular-nums">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()}
          </span>
          <div className="join">
            <button
              className="btn btn-sm join-item"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Previous
            </button>
            <button
              className="btn btn-sm join-item"
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              disabled={page >= lastPage}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
